import { memo } from 'react';
import { ResponsivePie } from '@nivo/pie';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { useUserMonthlyExperiences } from '../../../../hooks/analytics/useUserUtilization';
import { ChartWrapper } from '../../shared/ChartWrapper';
import { DarkTooltip } from '../../shared/Tooltip';
import { CHART_THEMES } from '../../config';
import { PieChart, X } from 'lucide-react';
import { useIsMobile } from '../../../../hooks/useIsMobile';

interface MonthExperienceBreakdownProps {
  firstName: string;
  lastName: string;
  monthYear: string;
  filters?: BaseAnalyticsFilter;
  onClear: () => void;
}

/** Converts "YYYY-MM" to "March 2026" */
function formatMonthYearFull(monthYear: string): string {
  const [year, month] = monthYear.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export const MonthExperienceBreakdown = memo(function MonthExperienceBreakdown({
  firstName,
  lastName,
  monthYear,
  filters,
  onClear,
}: MonthExperienceBreakdownProps) {
  const isMobile = useIsMobile();
  const { experiences, isLoading, isError } = useUserMonthlyExperiences({
    firstName,
    lastName,
    monthYear,
    filters,
    enabled: Boolean(monthYear),
  });

  const totalSessions = experiences.reduce((sum, e) => sum + e.session_count, 0);

  const pieData = experiences.map((entry, index) => ({
    id: entry.experience_title,
    label: entry.experience_title,
    value: entry.session_count,
    minutes: Math.round(entry.total_minutes),
    percentage: totalSessions > 0 ? (entry.session_count / totalSessions) * 100 : 0,
    color: `hsl(${(index * 360) / Math.max(experiences.length, 1)}, 70%, 60%)`,
  }));

  return (
    <div className="mt-6 rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface)]">
      <div className="flex items-center justify-between border-b border-[var(--anl-border)] p-4">
        <h3 className={`flex min-w-0 flex-1 items-center gap-2 font-semibold text-[var(--anl-text)] ${isMobile ? 'text-sm' : 'text-lg'}`}>
          <PieChart className={`flex-shrink-0 text-[var(--anl-text-muted)] ${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
          <span className="min-w-0 truncate">
            Session Distribution —{' '}
            <span className="text-[var(--anl-text-muted)]">{formatMonthYearFull(monthYear)}</span>
          </span>
        </h3>
        <button
          onClick={onClear}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-sm text-[var(--anl-text-muted)] transition-colors hover:bg-[var(--anl-surface-elev)] hover:text-[var(--anl-text)]"
          aria-label="Back to overview"
        >
          <X className="h-4 w-4" />
          Clear
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-cyan-500" />
        </div>
      )}

      {isError && (
        <div className="flex items-center justify-center py-12 text-[var(--anl-text-muted)]">
          <p className="text-sm">Failed to load experience data for this month.</p>
        </div>
      )}

      {!isLoading && !isError && pieData.length === 0 && (
        <div className="flex items-center justify-center py-12 text-[var(--anl-text-muted)]">
          <p className="text-sm">No experience data for this month.</p>
        </div>
      )}

      {!isLoading && !isError && pieData.length > 0 && (
        <ChartWrapper>
          <ResponsivePie
            data={pieData}
            margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
            innerRadius={0.5}
            padAngle={1}
            cornerRadius={2}
            activeOuterRadiusOffset={8}
            colors={{ datum: 'data.color' }}
            borderWidth={1}
            borderColor={{ from: 'color', modifiers: [['darker', 0.3]] }}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor="#e5e7eb"
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: 'color', modifiers: [['darker', 0.4]] }}
            arcLabelsSkipAngle={10}
            arcLabelsTextColor="#ffffff"
            arcLabelsRadiusOffset={0.6}
            theme={{
              ...CHART_THEMES.default,
              labels: { text: { fontSize: 13, fill: '#ffffff' } },
            }}
            tooltip={({ datum }) => (
              <DarkTooltip>
                <div
                  className="mb-1 max-w-[240px] truncate text-xs text-[var(--anl-text-muted)]"
                  title={String(datum.label)}
                >
                  {String(datum.label)}
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-[var(--anl-text-muted)]">Sessions:</span>
                    <span className="font-semibold text-[var(--anl-text)]">
                      {datum.value.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-[var(--anl-text-muted)]">Minutes:</span>
                    <span className="font-semibold text-[var(--anl-text)]">
                      {(datum.data as any).minutes}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-[var(--anl-text-muted)]">Share:</span>
                    <span className="font-semibold text-[var(--anl-text)]">
                      {(datum.data as any).percentage.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </DarkTooltip>
            )}
          />
        </ChartWrapper>
      )}
    </div>
  );
});
